import { lazy, Suspense, useState } from "react";
import {
  Briefcase,
  Check,
  Coins,
  CreditCard,
  Languages,
  Ruler,
  Tags,
} from "lucide-react";

const ServicesStep = lazy(() => import("./ServicesStep"));
const PaymentMethodStep = lazy(() => import("./PaymentMethodStep"));
const LanguagePairsStep = lazy(() => import("./LanguagePairsStep"));
const UnitsStep = lazy(() => import("./UnitsStep"));
const SpecializationsStep = lazy(() => import("./SpecializationsStep"));
const CurrenciesStep = lazy(() => import("./CurrenciesStep"));

const stepConfigs = [
  { name: "Services", icon: Briefcase, Component: ServicesStep, dataKey: "services" },
  {
    name: "Language Pairs",
    icon: Languages,
    Component: LanguagePairsStep,
    dataKey: "languagePairs",
  },
  { name: "Units", icon: Ruler, Component: UnitsStep, dataKey: "units" },
  {
    name: "Specializations",
    icon: Tags,
    Component: SpecializationsStep,
    dataKey: "specializations",
  },
  { name: "Currencies", icon: Coins, Component: CurrenciesStep, dataKey: "currencies" },
  {
    name: "Payment Methods",
    icon: CreditCard,
    Component: PaymentMethodStep,
    dataKey: "paymentMethods",
  },
];

const Stepper = ({ steps, currentStep, highestStep, goToStep }) => (
  <ol className="grid grid-cols-3 lg:grid-cols-6 gap-2">
    {steps.map((step, index) => {
      const isCurrent = index === currentStep;
      const isCompleted = !isCurrent && index < highestStep;
      const isClickable = index <= highestStep && !isCurrent;
      const Icon = isCompleted ? Check : step.icon;

      return (
        <li key={step.name}>
          <button
            type="button"
            onClick={() => isClickable && goToStep(index)}
            disabled={!isClickable}
            className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-default ${
              isCurrent
                ? "border-brand-300 bg-brand-50 ring-1 ring-brand-300"
                : isCompleted
                ? "border-gray-100 bg-white hover:border-brand-200 hover:bg-brand-50/50"
                : "border-dashed border-gray-200 bg-gray-50/60"
            }`}
          >
            <span
              className={`shrink-0 grid place-items-center w-7 h-7 rounded-md ${
                isCurrent
                  ? "bg-brand-600 text-white"
                  : isCompleted
                  ? "bg-success-100 text-success-800"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </span>
            <span className="min-w-0">
              <span
                className={`block text-[10px] font-semibold uppercase tracking-widest ${
                  isCurrent ? "text-brand-600" : "text-gray-400"
                }`}
              >
                Step {index + 1}
              </span>
              <span
                className={`block truncate text-[13px] font-medium ${
                  isCurrent ? "text-gray-900" : isCompleted ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {step.name}
              </span>
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);

const SetupWizard = ({ onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highestStepVisited, setHighestStepVisited] = useState(0);

  const [wizardData, setWizardData] = useState({
    services: [],
    paymentMethods: [],
    languagePairs: [],
    units: [],
    specializations: [],
    currencies: [],
  });

  const nextStep = () => {
    const newStep = currentStep + 1;
    if (newStep < stepConfigs.length) {
      setCurrentStep(newStep);
      setHighestStepVisited(Math.max(highestStepVisited, newStep));
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex <= highestStepVisited) {
      setCurrentStep(stepIndex);
    }
  };

  const { Component, dataKey } = stepConfigs[currentStep];
  const isLastStep = currentStep === stepConfigs.length - 1;
  const progress = Math.round((highestStepVisited / stepConfigs.length) * 100);

  // On desktop the wizard is pinned to the space MainLayout leaves under the
  // navbar (4rem) and its own padding, so the page itself never scrolls.
  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100vh-8rem)] lg:min-h-[560px]">
      <div className="shrink-0 bg-white border border-gray-100 rounded-xl shadow-card px-4 py-3 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold tracking-tight text-gray-900">
              Set up your workspace
            </h1>
            <p className="text-xs text-gray-500">
              You can change all of this later under System Values.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs font-medium text-gray-500">
              Step {currentStep + 1} of {stepConfigs.length}
            </span>
            <div className="hidden sm:block w-32 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <Stepper
          steps={stepConfigs}
          currentStep={currentStep}
          highestStep={highestStepVisited}
          goToStep={goToStep}
        />
      </div>

      <div className="flex-1 min-h-0">
        <Suspense
          fallback={
            <div className="h-full bg-white border border-gray-100 rounded-xl shadow-card py-16 text-center text-[13px] text-gray-400">
              Loading...
            </div>
          }
        >
          <Component
            data={wizardData[dataKey]}
            setData={(data) =>
              setWizardData({ ...wizardData, [dataKey]: data })
            }
            onNext={isLastStep ? () => onSubmit(wizardData) : nextStep}
            onBack={currentStep > 0 ? prevStep : undefined}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default SetupWizard;
